import { useRef, useState, type ReactNode } from "react";
import { At, Smiley } from "@phosphor-icons/react";
import {
  Button,
  cn,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
  MentionMenu,
  ReactionPicker,
  SimpleTooltip,
  useMentions,
} from "@hulahoop/ui";
import { commentMentionBody } from "@/utils/commentMentions";
import type { CommentPerson } from "@/domain/comment";
import type { CommentsLabels } from "@/domain/types/commentsLabels";

export function CommentsComposer({
  avatar,
  placeholder,
  actionLabel,
  people,
  labels,
  onSubmit,
  disabled = false,
}: {
  avatar: ReactNode;
  placeholder: string;
  actionLabel: string;
  people: CommentPerson[];
  labels: CommentsLabels;
  onSubmit: (body: string) => Promise<boolean>;
  disabled?: boolean;
}) {
  const [text, setText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const selectedPeople = useRef<CommentPerson[]>([]);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const candidates = people.map((person) => ({ id: person.uuid, name: person.name }));
  const mentions = useMentions({
    value: text,
    onChange: setText,
    people: candidates,
    textareaRef: areaRef,
    insertAs: (person) => {
      const selected = people.find((candidate) => candidate.uuid === person.id);
      if (selected)
        selectedPeople.current = [
          ...selectedPeople.current.filter((candidate) => candidate.name !== selected.name),
          selected,
        ];
      return `@${person.name} `;
    },
  });
  const handleSubmit = async () => {
    if (!text.trim() || isSending || disabled) return;
    setIsSending(true);
    try {
      if (await onSubmit(commentMentionBody(text.trim(), selectedPeople.current))) {
        setText("");
        selectedPeople.current = [];
      }
    } finally {
      setIsSending(false);
    }
  };
  return (
    <div ref={boxRef} data-slot="comment-composer">
      {people.length > 0 && (
        <MentionMenu
          {...mentions.menuProps}
          anchor={boxRef}
          open={mentions.open}
          labels={{
            title: () => labels.mention_title,
            empty: labels.mention_empty,
            menu: labels.mention,
          }}
        />
      )}
      <InputGroup className="h-auto flex-col items-stretch">
        <div className="flex w-full items-start gap-2 px-2.5 pt-2">
          {avatar}
          <InputGroupTextarea
            ref={areaRef}
            rows={1}
            placeholder={placeholder}
            value={text}
            aria-label={placeholder}
            disabled={isSending || disabled}
            className="min-h-0 px-0 py-0.5"
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (mentions.onKeyDown(event)) return;
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void handleSubmit();
              }
            }}
          />
        </div>
        <InputGroupAddon align="block-end" className="gap-0.5">
          <ReactionPicker
            tooltip={labels.emoji}
            align="start"
            pickLabel={labels.insertEmoji}
            onPick={(emoji) => {
              setText((value) => value + emoji);
              areaRef.current?.focus();
            }}
            trigger={
              <InputGroupButton size="icon-xs" aria-label={labels.emoji} nativeButton>
                <Smiley />
              </InputGroupButton>
            }
          />
          {people.length > 0 && (
            <SimpleTooltip label={labels.mention}>
              <InputGroupButton
                size="icon-xs"
                aria-label={labels.mention}
                onClick={mentions.startMention}
              >
                <At />
              </InputGroupButton>
            </SimpleTooltip>
          )}
          <span className="ml-auto">
            <Button
              variant={text.trim() ? "cta" : "ghost"}
              size="xs"
              disabled={isSending || disabled}
              aria-disabled={!text.trim()}
              className={cn(!text.trim() && "text-muted-foreground")}
              onClick={() => void handleSubmit()}
            >
              {actionLabel}
            </Button>
          </span>
        </InputGroupAddon>
      </InputGroup>
    </div>
  );
}
